<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-form
          ref="form"
          v-model="isFormValid"
        >
          <v-container fluid>
            <v-row v-if="!sendMode">
              <v-col>
                <p>
                  Do you want to send an engagement letter?
                </p>
                <v-radio-group
                  v-model="shouldSendLetter"
                  row
                  :rules="[rules.required]"
                >
                  <v-radio
                    label="Yes"
                    value="true"
                  />
                  <v-radio
                    label="No"
                    value="false"
                  />
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row v-if="shouldDisplayForm">
              <v-col v-if="!noEngagementLetters">
                <v-row>
                  <v-col>
                    <v-select
                      v-model="edited.engagement_letter.language_id"
                      outlined
                      item-text="name"
                      item-value="id"
                      persistent-hint
                      :items="languages"
                      label="Language *"
                      :rules="[rules.required]"
                    />
                  </v-col>
                </v-row>

                <v-row v-if="shouldDisplayTemplate">
                  <v-col>
                    <v-card>
                      <v-app-bar
                        flat
                        color="rgba(0, 0, 0, .75)"
                      >
                        <v-toolbar-title class="text-subtitle-1 white--text pl-0">
                          Engagement Letter Preview
                        </v-toolbar-title>
                      </v-app-bar>
                      <v-card-title>{{ legalCaseType.name }}</v-card-title>
                      <v-card-subtitle>{{ legalCaseType.description }}</v-card-subtitle>
                      <v-card-text style="max-height: 400px; overflow: scroll">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <p v-html="letterContent" />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-radio-group
                      v-model="edited.engagement_letter.notification_channel"
                      row
                      :rules="[rules.required]"
                    >
                      <v-radio
                        :disabled="!isTwilioSettingsSetup"
                        :label="phoneLabel"
                        value="text"
                      />
                      <v-radio
                        :disabled="!emailNotificationEnabled"
                        :label="emailLabel"
                        value="email"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <v-row v-if="edited.engagement_letter.notification_channel">
                  <v-col>
                    <v-select
                      v-model="edited.engagement_letter.recipient_identifier"
                      :items="partiesOptions"
                      label="Recipient *"
                      outlined
                      persistent-hint
                      :rules="[rules.required]"
                    />
                  </v-col>
                </v-row>

                <v-row v-if="edited.engagement_letter.recipient_identifier">
                  <v-col>
                    <v-select
                      v-model="edited.engagement_letter.notification_language"
                      :items="notificationLanguages"
                      label="Language of the notification *"
                      outlined
                      persistent-hint
                      :rules="[rules.required]"
                    />
                  </v-col>
                </v-row>

                <v-row v-if="edited.engagement_letter.notification_language">
                  <v-col>
                    <blockquote>
                      {{ edited.engagement_letter.notification_message }}
                    </blockquote>
                  </v-col>
                </v-row>
              </v-col>

              <v-row v-else-if="noEngagementLetters">
                <v-col>
                  <p>
                    You have no engagement letters. You need to create one.
                  </p>

                  <v-btn
                    @click="_ => openEngagementLetterCreateDialog()"
                  >
                    Create a letter
                  </v-btn>
                </v-col>
              </v-row>
            </v-row>
          </v-container>
        </v-form>
      </v-col>
    </v-row>

    <EngagementLetterCreateDialog
      v-if="shouldOpenEngagementLetterCreateDialog"
      v-model="shouldOpenEngagementLetterCreateDialog"
      @saved="_ => onEngagementLetterCreated()"
    />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isEmpty } from 'lodash'

import { partyOptionLabel, rules } from '@/utilities'
import { alertHandler } from '@/alertHandler'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'
import { USER_NAMESPACE } from '@/store/user'

import EngagementLetterCreateDialog from './LegalCaseEditorEngagementLetterDialog.vue'

export default {
  name: 'LegalCaseEditorEngagementLetter',

  components: {
    EngagementLetterCreateDialog,
  },

  props: {
    // This is set to true to recognize when this component is used standalone, i.e. usually outside of a case
    // creation wizard. Use cases can be found in Legal Case toolbar via the `Send engagement letter` action.
    sendMode: {
      default: false,
      type: Boolean,
    },

    valid: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      notificationLanguages: [
        { text: 'English', value: 'en' },
        { text: 'Spanish', value: 'es' },
      ],

      parties: [],
      rules,
      shouldOpenEngagementLetterCreateDialog: false,
      shouldSendLetter: false,
    }
  },

  asyncComputed: {
    async primaryAttorney () {
      if (!this.edited.primary_attorney_id) return

      return await this.getUser(this.edited.primary_attorney_id)
    },
  },

  computed: {
    emailLabel () {
      let label = 'Send by email'
      if (!this.emailNotificationEnabled) label += ' (Notifications by email are not enabled.)'
      return label
    },

    isFormValid: {
      get () { return this.valid },
      set (val) { this.$emit('update:valid', val) },
    },

    isTwilioSettingsSetup () {
      return !!this.features?.twilio?.enabled
    },

    languages () {
      return this.getCachedDropdown('legal_cases/engagement_letters/languages')
    },

    legalCaseType () {
      if (!this.edited) return

      return this.legalCaseTypesOptions?.find(l => l.value === this.edited.legal_case_type_id)
    },

    letterContent () {
      return this.edited.engagement_letter.content?.replaceAll('\n', '<br />')
    },

    noEngagementLetters () {
      return this.languages.length === 0
    },

    notificationContent () {
      let content

      switch (this.edited.engagement_letter.notification_language) {
        case 'en':
          content = `
           Hello! A new case ${this.legalCaseType.name} ID: {legal_case_id} has been created for you by provider ${this.primaryAttorney.first_name} ${this.primaryAttorney.last_name}. You must sign a digital engagement to continue. Contact the provider prior to signing if you have questions at ${this.primaryAttorney.email}. Please open the link, review and sign by typing your name. {link}
`
          break
        case 'es':
          content = `
          ¡Hola! El proveedor ${this.primaryAttorney.first_name} ${this.primaryAttorney.last_name} ha creado un nuevo caso ${this.legalCaseType.name} con ID {legal_case_id}. Debe firmar un compromiso digital para continuar. Comuniquese con el proveedor al email ${this.primaryAttorney.email} antes de firmar si tiene preguntas. Abra el enlace, revise y firme escribiendo su nombre. {link}
`
          break
      }

      return content
    },

    partiesOptions () {
      return this.parties.map(party => ({
        disabled: !this.partyCanBeRecipient(party),
        text: partyOptionLabel.bind(this)(party),
        value: party.identifier,
      }))
    },

    phoneLabel () {
      let label = 'Send by text'
      if (!this.isTwilioSettingsSetup) label += ' (Twilio is not set up. You can not send a text.)'
      return label
    },

    shouldDisplayForm () {
      return this.shouldSendLetter === 'true'
    },

    shouldDisplayTemplate () {
      return this.edited.engagement_letter.language_id && this.edited.legal_case_type_id
    },

    ...mapGetters([
      'currentUser',
      'legalCaseTypesOptions',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'edited',
      'emailNotificationEnabled',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
      'getCachedDropdown',
    ]),
  },

  watch: {
    'edited.engagement_letter.language_id': {
      handler (value) {
        if (value) this.getEngagementLetterContent(value)
        else {
          this.edited.engagement_letter.notification_channel = null
          this.edited.engagement_letter.notification_language = null
          this.shouldSendLetter = 'false'
        }
      },

      immediate: true,
    },

    'edited.engagement_letter.notification_channel' () {
      this.edited.engagement_letter.recipient_identifier = null
    },

    'edited.engagement_letter.notification_language' (value) {
      this.edited.engagement_letter.notification_message = null
      if (!value) return

      this.edited.engagement_letter.notification_message = this.notificationContent
    },

    'edited.engagement_letter.recipient_identifier' (value) {
      this.edited.engagement_letter.notification_language = null
    },

    'edited.parties' () {
      const partyIds = (this.edited.parties || []).
        reduce((res, party) => {
          if (party?.id) res.push(party.id)
          return res
        }, [])

      if (!partyIds.length) {
        this.parties = this.edited.parties || []
        return
      }

      // We are fetching additional data for existing parties that have been selected,
      // i.e. phones and emails
      this.getPartiesWithOptions({ options: { include: ['emails', 'phones'] }, partyIds }).
        then(parties => {
          // We are matching the fetched data with the edited.parties data and we update
          // the edited.parties data to ease the rest of the flow
          this.parties = this.edited.parties.map(party => {
            const existingParty = parties.find(p => p.id === party.id)
            if (existingParty) {
              party.primary_text_number = existingParty.primary_text_number
              party.primary_email = existingParty.primary_email
            }

            return party
          })
        })
    },

    shouldSendLetter (value) {
      if (value === 'true') return

      this.edited.engagement_letter = {}
    },
  },

  created () {
    this.getFeatures()
    this.getEmailNotificationEnabled()
    this.getDropdownOptions({ force: true, options: false, path: 'legal_cases/engagement_letters/languages' })

    if (this.sendMode) this.shouldSendLetter = 'true'
  },

  methods: {
    onEngagementLetterCreated () {
      this.getDropdownOptions({ force: true, options: false, path: 'legal_cases/engagement_letters/languages' })
    },

    openEngagementLetterCreateDialog () {
      this.shouldOpenEngagementLetterCreateDialog = true
    },

    partyCanBeRecipient (party) {
      if (this.edited.engagement_letter.notification_channel === 'email') return this.partyHasEmail(party)
      if (this.edited.engagement_letter.notification_channel === 'text') return this.partyHasPreferredTextNumber(party)
    },

    partyHasEmail (party) {
      return !isEmpty(party.primary_email?.address)
    },

    partyHasPreferredTextNumber (party) {
      // TODO Remove once whatsapp support fully works
      return !isEmpty(party.primary_text_number?.phone_number) && party.primary_text_number.phone_type !== 'wa'
    },

    validate () {
      this.$refs.form.validate()
    },

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'getEmailNotificationEnabled',
      'getEngagementLetterContent',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'getPartiesWithOptions',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
      'getFeatures',
      'getOptions',
    ]),

    ...mapActions(USER_NAMESPACE, [
      'getUser',
    ]),
  },
}
</script>
